import { createContext, useContext, ReactNode } from 'react'

import { useAuthStore } from '@hooks/useAuthStore'
import { useClinicTimezone } from '@hooks/useClinicTimezone'
import { DEFAULT_CLINIC_TZ, formatClinicDate, formatClinicTime } from '@lib/clinicDate'

// One source of truth for the clinic's active clinical-availability region.
// When the availability timezone changes, every consumer of useClinicFormat()
// re-renders its dates in that region's format.
const ClinicTimezoneContext = createContext<string>(DEFAULT_CLINIC_TZ)

export function ClinicTimezoneProvider({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user)
  const timezone = useClinicTimezone(user?.id)
  return (
    <ClinicTimezoneContext.Provider value={timezone}>
      {children}
    </ClinicTimezoneContext.Provider>
  )
}

// Region-aware date/time formatters bound to the active clinical-availability
// timezone. Use this instead of ad-hoc toLocaleDateString calls so dates follow
// the clinic's region.
export function useClinicFormat() {
  const timezone = useContext(ClinicTimezoneContext)
  return {
    timezone,
    formatDate: (iso: string) => formatClinicDate(iso, timezone),
    formatTime: (iso: string) => formatClinicTime(iso, timezone),
    formatDateTime: (iso: string) =>
      `${formatClinicDate(iso, timezone)} ${formatClinicTime(iso, timezone)}`,
  }
}
